import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { Directive, ElementRef, input, OnDestroy } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Renderer } from '@df/ng/renderer';
import { BodyClassService } from '@df/ui/body-class.service';
import { debounceTime, switchMap } from 'rxjs';
import { Subscription } from 'rxjs/internal/Subscription';
import { PAGE_HEADER_VERSION_STATE } from './page-header.config';
import { NPageHeader } from './page-header.interface';
import { PageHeaderService } from './page-header.service';

@Directive({
    selector: '[pageHeaderBody]',
    standalone: true
})
export class PageHeaderBodyDirective implements OnDestroy {
    pageHeaderBody = input(true);
    protected enabled$ = toObservable(this.pageHeaderBody);

    private _enabled = true;
    private subscriptions = new Subscription();

    /**
     * Current state
     */
    private state?: NPageHeader.IVersionState;

    constructor(
        private pageHeaderService: PageHeaderService,
        private elementRef: ElementRef,
        private renderer: Renderer,
        private bodyClassService: BodyClassService
    ) {
        // observe page header state only when enabled
        this.subscriptions.add(
            this.enabled$
                .pipe(
                    debounceTime(0),
                    switchMap(enabled => {
                        this._enabled = coerceBooleanProperty(enabled);
                        return this.pageHeaderService.getState();
                    })
                )
                .subscribe(state => {
                    this.state = state;
                    this.update();
                })
        );
    }

    ngOnDestroy(): void {
        this.bodyClassService.remove(NPageHeader.EClasses.hideUp, this);
        this.bodyClassService.remove(NPageHeader.EClasses.transparent, this);
        this.subscriptions.unsubscribe();
    }

    private update() {
        const state = this.state;

        if (!state) {
            return;
        }

        const el = this.elementRef.nativeElement;
        let transparent = !!state.transparent;
        let lightMode = !!state.transparent && !!state.lightMode;
        let hideUp = !!state.hideUp;

        if (!this._enabled) {
            transparent = !!PAGE_HEADER_VERSION_STATE.transparent;
            lightMode = !!PAGE_HEADER_VERSION_STATE.lightMode;
            hideUp = !!PAGE_HEADER_VERSION_STATE.hideUp;
        }

        this.renderer.setClass(el, NPageHeader.EClasses.transparent, transparent);
        this.renderer.setClass(el, NPageHeader.EClasses.lightMode, lightMode);
        this.renderer.setClass(el, NPageHeader.EClasses.hideUp, hideUp);

        this.bodyClassService.set(NPageHeader.EClasses.hideUp, this, hideUp);
        this.bodyClassService.set(NPageHeader.EClasses.transparent, this, transparent);
    }
}
